import Debug from '../utils/Debug.js';

/**
 * NPCSync — Authority-driven NPC position sync for multiplayer.
 *
 * Only the authority peer (oldest) runs NPC AI (behavior, pathfinding, physics).
 * All other peers set isNetworkControlled=true on NPCs and interpolate positions
 * using velocity-predicted lerp (same algorithm as PlayerSync).
 *
 * Authority broadcasts NPC positions at 4Hz (250ms). Every 5s, a full behavior
 * state snapshot is included for smooth authority handoff when peers leave.
 */
export default class NPCSync {
  static BROADCAST_INTERVAL_MS = 250;   // 4Hz position broadcasts
  static SNAPSHOT_INTERVAL_MS = 5000;   // Behavior state for authority handoff
  static INTERPOLATION_SPEED = 10;      // Lerp speed (matches PlayerSync)
  static MAX_VELOCITY = 15;             // Velocity cap (matches PlayerSync)
  static STALE_THRESHOLD_MS = 3000;     // Force idle if no updates for 3s

  /**
   * @param {NPCManager} npcManager
   * @param {NetworkManager} networkManager
   */
  constructor(npcManager, networkManager) {
    this.npcManager = npcManager;
    this.network = networkManager;
    this._isAuthority = false;

    // Authority-side state
    this._broadcastTimer = 0;
    this._snapshotTimer = 0;

    // Non-authority-side state: per-NPC interpolation data
    // npcId -> { targetState, velocity, lastUpdateTime, behaviorSnapshot }
    this._remoteNPCs = new Map();

    // Saved behavior snapshots for authority handoff.
    // setAuthority(true) clears _remoteNPCs, so we preserve snapshots here
    // for restoreBehaviorStates() to consume after the handoff.
    this._pendingBehaviorSnapshots = null;

    // Set by main.js for instance-aware filtering (survives dungeon transitions)
    this._dungeonInstanceManager = null;

    this.network.onNPCStates = (data) => this._onReceive(data);
  }

  /**
   * Update authority status. Called on peer join/leave.
   * Toggles isNetworkControlled on all NPCs and resets interpolation state.
   * @param {boolean} isAuthority
   */
  setAuthority(isAuthority) {
    const was = this._isAuthority;
    this._isAuthority = isAuthority;

    if (was === isAuthority) return;

    // Save behavior snapshots BEFORE clearing _remoteNPCs.
    // restoreBehaviorStates() needs these after authority handoff.
    if (isAuthority && !was) {
      this._pendingBehaviorSnapshots = new Map();
      for (const [id, remote] of this._remoteNPCs) {
        if (remote.behaviorSnapshot) {
          this._pendingBehaviorSnapshots.set(id, remote.behaviorSnapshot);
        }
      }
    }

    for (const [id, { character }] of this.npcManager.npcs) {
      if (isAuthority) {
        // We are authority: re-enable local AI
        character.isNetworkControlled = false;
        this._remoteNPCs.delete(id);
      } else {
        // Someone else is authority: disable local AI, start interpolation
        character.isNetworkControlled = true;
        if (character.pathFollower && character.pathFollower.isFollowing) {
          character.pathFollower.stop();
        }
        // Seed interpolation at current position (no snap)
        this._remoteNPCs.set(id, {
          targetState: {
            x: character.x, y: character.y, z: character.z,
            a: character.facingAngle,
            m: character.isMoving
          },
          velocity: null,
          lastUpdateTime: Date.now(),
          behaviorSnapshot: null
        });
      }
    }

    this._broadcastTimer = 0;
    this._snapshotTimer = 0;

    // Broadcast immediately so followers get data fast
    if (isAuthority && this.network.isConnected) {
      this._broadcastPositions();
    }
  }

  /**
   * Ensure all current NPCs have the correct isNetworkControlled flag.
   * Called after NPCs are spawned from remote world state (fromJSON is async,
   * so NPCs may not exist when setAuthority was originally called).
   */
  syncNPCFlags() {
    for (const [id, { character }] of this.npcManager.npcs) {
      if (this._isAuthority) {
        character.isNetworkControlled = false;
      } else if (!character.isNetworkControlled) {
        character.isNetworkControlled = true;
        if (character.pathFollower && character.pathFollower.isFollowing) {
          character.pathFollower.stop();
        }
        // Seed interpolation at current position if not already tracked
        if (!this._remoteNPCs.has(id)) {
          this._remoteNPCs.set(id, {
            targetState: {
              x: character.x, y: character.y, z: character.z,
              a: character.facingAngle,
              m: character.isMoving
            },
            velocity: null,
            lastUpdateTime: Date.now(),
            behaviorSnapshot: null
          });
        }
      }
    }
  }

  /**
   * Restore behavior states from snapshots saved during authority handoff.
   * Called by main.js when this peer becomes authority after the old authority leaves.
   * Uses _pendingBehaviorSnapshots which were captured by setAuthority() before
   * it cleared _remoteNPCs.
   */
  restoreBehaviorStates() {
    const snapshots = this._pendingBehaviorSnapshots;
    this._pendingBehaviorSnapshots = null;
    if (!snapshots || snapshots.size === 0) return;

    let restored = 0;
    for (const [npcId, snapshot] of snapshots) {
      const entry = this.npcManager.npcs.get(npcId);
      if (!entry || !entry.behavior) continue;
      entry.behavior.setState(snapshot);
      restored++;
    }
    if (restored > 0) {
      Debug.log('NPCSync', `Restored behavior states for ${restored} NPC(s) after authority handoff`);
    }
  }

  /**
   * Called every frame.
   * @param {number} deltaTime - Seconds since last frame
   */
  update(deltaTime) {
    if (this._isAuthority) {
      this._updateAsAuthority(deltaTime);
    } else {
      this._updateAsFollower(deltaTime);
    }
  }

  // ─── NPC actions (dialog, combat) ─────────────────────────────────

  /**
   * Handle an NPC action from a remote peer (dialog, combat).
   * Routes to specific handlers based on action type.
   * @param {Object} data - Action data ({ type, npcId, ... })
   */
  handleAction(data, peerId) {
    // Skip NPC actions from peers in a different instance
    const dim = this._dungeonInstanceManager;
    if (dim && peerId) {
      const myInst = dim._isInDungeon ? dim._instanceId : null;
      const theirInst = dim._peerInstances.get(peerId) || null;
      if (myInst !== theirInst) return;
    }

    // Combat actions don't require the NPC to exist locally (see _handleRemoteDeath fallback)
    if (data.type === 'npc-damage') {
      this._handleRemoteDamage(data);
      return;
    }
    if (data.type === 'npc-death') {
      this._handleRemoteDeath(data);
      return;
    }

    const entry = this.npcManager.npcs.get(data.npcId);
    if (!entry) return;

    const char = entry.character;

    if (data.type === 'npc-dialog-start') {
      // Freeze NPC: stop movement, face toward the player who started dialog
      if (char.pathFollower && char.pathFollower.isFollowing) {
        char.pathFollower.stop();
      }
      char.velocity.set(0, 0, 0);
      char.setMovementState(false, false, true);
      if (char.behavior) {
        char.behavior._dialogPaused = true;
      }
      // Face toward the player
      if (data.px != null && data.pz != null) {
        const angle = Math.atan2(data.pz - char.z, data.px - char.x) * (180 / Math.PI);
        char.setFacing(((angle % 360) + 360) % 360);
      }
    } else if (data.type === 'npc-dialog-end') {
      // Resume NPC AI
      if (char.behavior) {
        char.behavior._dialogPaused = false;
        char.behavior._enterIdle();
      }
    }
  }

  /**
   * Authority receives damage request from a non-authority peer's attack.
   * Applies damage, knockback, i-frames, and triggers death if health <= 0.
   * Works in both dungeon and normal world multiplayer.
   * @private
   */
  _handleRemoteDamage(data) {
    if (!this._isAuthority) return;

    // Level filtering for dungeon mode
    const dim = window.game?.dungeonInstanceManager;
    if (dim?._isInDungeon && data.levelIndex !== undefined && data.levelIndex >= 0) {
      if (data.levelIndex !== dim._myLevel) return;
    }

    const entry = this.npcManager.npcs.get(data.npcId);
    if (!entry) return;

    const char = entry.character;
    if (!char.combatStats || char.combatStats.isDead) return;
    if (char.combatStats.isInvulnerable) return;

    const stats = char.combatStats;
    stats.health -= data.damage;

    // Trigger i-frames
    stats.isInvulnerable = true;
    stats.invulnerabilityTimer = stats.invulnerabilityDuration;

    // Knockback from attacker position
    if (data.ax !== undefined && data.az !== undefined) {
      const dx = char.x - data.ax;
      const dz = char.z - data.az;
      const dist = Math.sqrt(dx * dx + dz * dz) || 1;
      stats.knockbackVelocity.x = (dx / dist) * 1.5;
      stats.knockbackVelocity.z = (dz / dist) * 1.5;
    }

    // Visual feedback on authority
    if (char.sprite) char.sprite._hitFlashTimer = 0.1;

    // Set threat position so NPC AI targets the attacker
    if (entry.behavior && data.ax !== undefined && data.az !== undefined) {
      entry.behavior.setThreatPosition(data.ax, data.az);
    }

    // Hit reaction or death
    if (stats.health > 0) {
      const hitAnim = char.isLPC ? 'taking-punch'
        : char.characterName === 'bear' ? 'going-to-sleep'
        : char.characterName === 'hellrat' ? 'eating'
        : 'taking-punch';
      char.triggerAction(hitAnim);
    } else {
      stats.health = 0;
      // Use CombatManager's _handleDeath for consistent death handling
      // (loot drop, corpse fade, quest, death broadcast to all peers)
      const fakeAttacker = { x: data.ax ?? char.x, z: data.az ?? char.z, isNPC: false };
      window.game?.combatManager?._handleDeath(char, fakeAttacker);
    }
  }

  /**
   * Follower receives NPC death broadcast from authority.
   * Applies death state, animation, loot drop, and corpse fade.
   * Works in both dungeon and normal world multiplayer.
   * @private
   */
  _handleRemoteDeath(data) {
    // Track in dungeon state (for late joiners and chunk reload)
    const dim = window.game?.dungeonInstanceManager;
    if (dim?._isInDungeon) {
      dim._killedNPCs.add(data.npcId);
      window.game?.dungeonGenerator?.streamer?.markKilled(data.npcId);
    }

    // Level filtering for dungeon mode
    if (dim?._isInDungeon && data.levelIndex !== undefined && data.levelIndex >= 0) {
      if (data.levelIndex !== dim._myLevel) {
        // Different level — save objective completion for when we visit that level.
        // The kill is tracked in _killedNPCs above, but quest progress needs
        // to be saved so _restoreObjectiveState() can mark it complete.
        if (data.isBoss) {
          dim._saveRemoteObjectiveCompletion(data.levelIndex, 'kill_boss', null);
        }
        // Track kill count for kill_count objectives
        dim._saveRemoteKill(data.levelIndex);
        return;
      }
    }

    const entry = this.npcManager.npcs.get(data.npcId);
    if (entry) {
      const char = entry.character;
      if (char.combatStats) {
        char.combatStats.health = 0;
        char.combatStats.isDead = true;
      }

      // Death animation
      const deathAnim = char.isLPC ? 'falling-back-death'
        : char.characterName === 'bear' ? 'going-to-sleep'
        : char.characterName === 'hellrat' ? 'eating'
        : 'falling-back-death';
      char.triggerAction(deathAnim);

      // Stop movement
      if (char.pathFollower) char.pathFollower.stop();
      if (char.controller) char.controller.enabled = false;
      if (char.behavior) char.behavior._dialogPaused = true;

      // Spawn the same loot items that authority spawned (shared, not independent)
      if (data.loot && data.loot.length > 0) {
        for (const loot of data.loot) {
          window.game?.lootManager?.spawnNetworkItem(loot.id, loot.itemId, loot.x, loot.y, loot.z, 1, loot.instanceData || null);
        }
      }
      window.game?.questManager?.onEnemyKilled(char);

      // Corpse fade timer
      const cm = window.game?.combatManager;
      if (cm) {
        cm._deadEnemies.push({ character: char, timer: cm.CORPSE_FADE_TIME });
      }
    } else if (data.x !== undefined) {
      // NPC was despawned (chunk unloaded) — spawn loot from broadcast
      if (data.loot && data.loot.length > 0) {
        for (const loot of data.loot) {
          window.game?.lootManager?.spawnNetworkItem(loot.id, loot.itemId, loot.x, loot.y, loot.z, 1, loot.instanceData || null);
        }
      }
    }
  }

  // ─── Authority side ─────────────────────────────────────────────

  /**
   * Authority: broadcast NPC positions at 4Hz, snapshots every 5s.
   * @param {number} deltaTime
   * @private
   */
  _updateAsAuthority(deltaTime) {
    if (!this.network.isConnected) return;

    this._broadcastTimer += deltaTime * 1000;
    this._snapshotTimer += deltaTime * 1000;

    if (this._broadcastTimer >= NPCSync.BROADCAST_INTERVAL_MS) {
      this._broadcastTimer = 0;

      // Include behavior snapshots every 5s for authority handoff
      if (this._snapshotTimer >= NPCSync.SNAPSHOT_INTERVAL_MS) {
        this._snapshotTimer = 0;
        this._broadcastPositions(true);
      } else {
        this._broadcastPositions(false);
      }
    }
  }

  /**
   * Broadcast NPC positions (and optionally behavior state) to all peers.
   * @param {boolean} includeSnapshot - Include full behavior state
   * @private
   */
  _broadcastPositions(includeSnapshot = false) {
    const states = [];

    for (const [id, { character, behavior }] of this.npcManager.npcs) {
      const s = {
        id,
        x: Math.round(character.x * 100) / 100,
        y: Math.round(character.y * 100) / 100,
        z: Math.round(character.z * 100) / 100,
        a: Math.round(character.facingAngle),
        m: character.isMoving
      };
      // Include dungeon instance + level for cross-instance filtering
      if (this._dungeonInstanceManager?._isInDungeon) {
        s.lvl = this._dungeonInstanceManager.currentLevel;
        s.inst = this._dungeonInstanceManager._instanceId;
      }
      // Include health for dungeon NPCs so followers see health bars update
      if (character.combatStats) {
        s.hp = character.combatStats.health;
        s.maxHp = character.combatStats.maxHealth;
      }
      if (includeSnapshot) {
        s.bs = behavior.getState();
      }
      states.push(s);
    }

    if (states.length > 0) {
      this.network.broadcastNPCStates(states);
    }
  }

  // ─── Follower side ──────────────────────────────────────────────

  /**
   * Receive NPC states from authority.
   * @param {Array} data
   * @private
   */
  _onReceive(data) {
    if (this._isAuthority) return;
    if (!Array.isArray(data)) return;

    const now = Date.now();

    for (const s of data) {
      // Instance filtering: skip NPCs from different instances entirely
      const dim = this._dungeonInstanceManager;
      const myInst = dim?._isInDungeon ? dim._instanceId : null;
      const theirInst = s.inst || null;
      if (myInst !== theirInst) continue;

      // Level filtering: skip NPCs on different dungeon levels
      if (dim?._isInDungeon && s.lvl !== undefined) {
        if (s.lvl !== dim.currentLevel) continue;
      }

      let remote = this._remoteNPCs.get(s.id);

      // Ensure NPC is network-controlled (handles NPCs spawned after setAuthority)
      const entry = this.npcManager.npcs.get(s.id);
      if (entry && !entry.character.isNetworkControlled) {
        entry.character.isNetworkControlled = true;
        if (entry.character.pathFollower && entry.character.pathFollower.isFollowing) {
          entry.character.pathFollower.stop();
        }
      }

      if (!remote) {
        // First update for this NPC
        remote = {
          targetState: s,
          velocity: null,
          lastUpdateTime: now,
          behaviorSnapshot: null
        };
        this._remoteNPCs.set(s.id, remote);
        continue;
      }

      // Compute velocity from position delta (same as PlayerSync)
      const dt = (now - remote.lastUpdateTime) / 1000;
      if (dt > 0.01 && remote.targetState) {
        const cap = NPCSync.MAX_VELOCITY;
        remote.velocity = {
          x: Math.max(-cap, Math.min(cap, (s.x - remote.targetState.x) / dt)),
          y: Math.max(-cap, Math.min(cap, (s.y - remote.targetState.y) / dt)),
          z: Math.max(-cap, Math.min(cap, (s.z - remote.targetState.z) / dt))
        };
      }

      remote.targetState = s;
      remote.lastUpdateTime = now;

      // Store behavior snapshot for authority handoff
      if (s.bs) {
        remote.behaviorSnapshot = s.bs;
      }

      // Sync NPC health from authority (dungeon mode)
      if (entry && entry.character.combatStats && s.hp !== undefined) {
        entry.character.combatStats.health = s.hp;
        if (s.maxHp !== undefined) entry.character.combatStats.maxHealth = s.maxHp;
        // Update nameplate health bar
        if (entry.character._nameplateHpFill) {
          const pct = Math.max(0, s.hp / entry.character.combatStats.maxHealth) * 100;
          entry.character._nameplateHpFill.style.width = `${pct}%`;
        }
      }
    }
  }

  /**
   * Non-authority: interpolate NPCs toward authority positions each frame.
   * Uses velocity-predicted lerp (identical to PlayerSync algorithm).
   * @param {number} deltaTime
   * @private
   */
  _updateAsFollower(deltaTime) {
    const now = Date.now();

    for (const [npcId, remote] of this._remoteNPCs) {
      const entry = this.npcManager.npcs.get(npcId);
      if (!entry) continue;

      const char = entry.character;
      const target = remote.targetState;
      if (!target) continue;

      // Skip interpolation for NPCs frozen by dialog — hold at current position
      // so the NPC stays face-to-face with the player who started dialog
      if (entry.behavior && entry.behavior._dialogPaused) continue;

      const timeSinceUpdate = now - remote.lastUpdateTime;

      // Staleness: no updates for 3s → force idle, hold position
      if (timeSinceUpdate > NPCSync.STALE_THRESHOLD_MS) {
        if (char.isMoving) {
          char.isMoving = false;
          const idleAnim = char.animations?.idle || 'breathing-idle';
          if (char.animator && char.animator.currentState !== idleAnim) {
            char.animator.setState(idleAnim);
          }
        }
        remote.velocity = null;
        continue;
      }

      // Velocity-predicted interpolation
      let predictX = target.x;
      let predictY = target.y;
      let predictZ = target.z;

      if (remote.velocity && target.m) {
        const tSinceUpdate = timeSinceUpdate / 1000;
        const predictTime = Math.min(tSinceUpdate, 0.2); // Cap at 200ms
        predictX += remote.velocity.x * predictTime;
        predictY += remote.velocity.y * predictTime;
        predictZ += remote.velocity.z * predictTime;
      }

      const t = Math.min(NPCSync.INTERPOLATION_SPEED * deltaTime, 1);
      char.x += (predictX - char.x) * t;
      char.y += (predictY - char.y) * t;
      char.z += (predictZ - char.z) * t;

      // Snap facing angle
      char.facingAngle = target.a;

      // Drive animation from authority's isMoving flag
      if (char.animator && !char.animator.isPlayingAction) {
        if (target.m) {
          const walkAnim = char.animations?.walk || 'walking-8-frames';
          if (char.animator.currentState !== walkAnim) {
            char.animator.setState(walkAnim);
          }
          char.isMoving = true;
        } else {
          const idleAnim = char.animations?.idle || 'breathing-idle';
          if (char.animator.currentState !== idleAnim) {
            char.animator.setState(idleAnim);
          }
          char.isMoving = false;
        }
      }
    }
  }

  /**
   * Reset for room transition. Clears all remote NPC interpolation data.
   * Unlike destroy(), does not null out network callbacks.
   */
  reset() {
    this._remoteNPCs.clear();
    this._broadcastTimer = 0;
    this._snapshotTimer = 0;
  }

  /**
   * Clean up.
   */
  destroy() {
    this.network.onNPCStates = null;
    this._remoteNPCs.clear();

    // Reset all NPCs to local control
    for (const [, { character }] of this.npcManager.npcs) {
      character.isNetworkControlled = false;
    }
  }
}
